import type { TFunction } from 'i18next';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { RebalanceInsightDecisionSummary } from '../../types/insightNarrative.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import {
  isFundingSegregationNotApplied,
  isInactiveZeroAccountDecision,
} from '../audit/decisionPredicates';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';
import {
  AdvisorSituationKey,
  isCashSituationDuplicatedInChanges,
  OptimizerDecisionImpact,
  OptimizerDecisionStage,
  PORTFOLIO_CASH_SITUATION_KEYS,
} from './advisorSituationKeys';
import type { RebalanceChangeSummary } from './buildRebalanceChangeSummary';
import { takeFirst } from './compactInsightAiContext';
import {
  formatAdvisorDecisionAction,
  resolveAdvisorSituationKey,
} from './formatAdvisorInsightText';
import { sanitizeAdvisorSummaryForAi } from './formatAdvisorSummaryForAi';

export const AI_DECISION_SUMMARY_CAP = 15;
export const AI_DECISION_SUMMARY_MAX_CHARS = 500;

const AI_PIPELINE_STAGES_HIDDEN_UNLESS_BLOCKING = new Set<string>([
  OptimizerDecisionStage.ConstraintCompile,
  OptimizerDecisionStage.Rounding,
  OptimizerDecisionStage.TradeGeneration,
  OptimizerDecisionStage.ReturnsFetch,
  OptimizerDecisionStage.TransactionCost,
  OptimizerDecisionStage.FxCost,
  OptimizerDecisionStage.TaxLotSelection,
  OptimizerDecisionStage.HarvestScan,
  OptimizerDecisionStage.WashSaleCheck,
  OptimizerDecisionStage.Validation,
]);

export const isStructuralFundingDriftDecision = (decision: {
  stage: string;
  action: string;
}): boolean =>
  decision.stage === OptimizerDecisionStage.FundingSegregation &&
  /structural funding drift/i.test(decision.action);

const shouldIncludeDecisionForAi = (
  decision: {
    stage: string;
    action: string;
    impact?: string | null;
  },
  options: {
    hasValidationFailure: boolean;
    hasTaxLocationMoves: boolean;
    changes: RebalanceChangeSummary;
  },
): boolean => {
  const impact = decision.impact ?? OptimizerDecisionImpact.None;

  if (decision.stage === OptimizerDecisionStage.Turnover) {
    return false;
  }

  if (
    options.hasValidationFailure &&
    decision.stage === OptimizerDecisionStage.InvariantCheck
  ) {
    return false;
  }

  if (isInactiveZeroAccountDecision(decision)) {
    return false;
  }

  if (isFundingSegregationNotApplied(decision)) {
    return false;
  }

  if (
    decision.stage === OptimizerDecisionStage.TaxLocation &&
    options.hasTaxLocationMoves
  ) {
    return false;
  }

  const situationKey = resolveAdvisorSituationKey(decision);
  if (
    PORTFOLIO_CASH_SITUATION_KEYS.has(situationKey) &&
    isCashSituationDuplicatedInChanges(situationKey, options.changes)
  ) {
    return false;
  }

  if (
    situationKey === AdvisorSituationKey.CashRaise ||
    situationKey === AdvisorSituationKey.CashDeploy
  ) {
    return false;
  }

  if (
    AI_PIPELINE_STAGES_HIDDEN_UNLESS_BLOCKING.has(decision.stage) &&
    impact !== OptimizerDecisionImpact.Blocking
  ) {
    return false;
  }

  if (
    impact === OptimizerDecisionImpact.Blocking ||
    impact === OptimizerDecisionImpact.Material
  ) {
    return true;
  }

  if (
    impact === OptimizerDecisionImpact.Minor &&
    isStructuralFundingDriftDecision(decision)
  ) {
    return true;
  }

  return false;
};

const truncateSummary = (summary: string): string => {
  if (summary.length <= AI_DECISION_SUMMARY_MAX_CHARS) {
    return summary;
  }

  return `${summary.slice(0, AI_DECISION_SUMMARY_MAX_CHARS - 1).trimEnd()}…`;
};

export const buildPortfolioDecisionSummariesForAi = ({
  audit,
  changes,
  diffRows = [],
  subAccountsById = new Map(),
  taxLocationMoveCount = 0,
  t,
}: {
  audit?: OptimizerAuditV2Record;
  changes: RebalanceChangeSummary;
  diffRows?: WeightDiffDetailRow[];
  subAccountsById?: Map<string, ReportSubAccountDisplayMetadata>;
  taxLocationMoveCount?: number;
  t: TFunction;
}): RebalanceInsightDecisionSummary[] => {
  if (!audit) {
    return [];
  }

  const hasValidationFailure =
    audit.passed === false || (audit.validationErrors?.length ?? 0) > 0;
  const usedCashModeSubAccountIds = new Set<string>();

  const summaries = (audit.decisions ?? [])
    .filter((decision) =>
      shouldIncludeDecisionForAi(decision, {
        hasValidationFailure,
        hasTaxLocationMoves: taxLocationMoveCount > 0,
        changes,
      }),
    )
    .map((decision) => {
      const summary = formatAdvisorDecisionAction(
        {
          stage: decision.stage,
          action: decision.action,
          impact: decision.impact ?? OptimizerDecisionImpact.Material,
        },
        subAccountsById,
        audit,
        t,
        { diffRows, usedCashModeSubAccountIds },
      );

      if (!summary) {
        return null;
      }

      return {
        stage: resolveAdvisorSituationKey(decision),
        impact: decision.impact ?? OptimizerDecisionImpact.Material,
        summary: truncateSummary(sanitizeAdvisorSummaryForAi(summary)),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

  return takeFirst(summaries, AI_DECISION_SUMMARY_CAP);
};
